import React, { memo, useCallback, useRef, useState } from 'react';
import {
  EdgeProps, getBezierPath, EdgeLabelRenderer, BaseEdge, useReactFlow,
} from '@xyflow/react';
import { useDiagramStore } from '../store/diagramStore';

const FlowEdge: React.FC<EdgeProps> = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  label, style, markerEnd, data, selected,
}) => {
  const { screenToFlowPosition } = useReactFlow();
  const updateEdgeWaypoint = useDiagramStore(s => s.updateEdgeWaypoint);
  const clearEdgeWaypoint = useDiagramStore(s => s.clearEdgeWaypoint);
  const removeConnection = useDiagramStore(s => s.removeConnection);

  const [isHovered, setIsHovered] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  const showHover = useCallback(() => {
    clearTimeout(hideTimer.current);
    setIsHovered(true);
  }, []);

  const hideHover = useCallback(() => {
    hideTimer.current = setTimeout(() => setIsHovered(false), 200);
  }, []);

  const edgeData = data as Record<string, unknown>;
  const connType = edgeData?.connectionType as string || 'normal';
  const waypoint = edgeData?.waypoint as { x: number; y: number } | null;

  const color = connType === 'error' ? '#e53935' :
                connType === 'conditional' ? '#f9a825' :
                (style?.stroke as string) || '#888';

  let edgePath: string;
  let labelX: number;
  let labelY: number;
  let midX: number;
  let midY: number;

  if (waypoint) {
    edgePath = `M ${sourceX} ${sourceY} L ${waypoint.x} ${waypoint.y} L ${targetX} ${targetY}`;
    labelX = (sourceX + waypoint.x) / 2;
    labelY = (sourceY + waypoint.y) / 2;
    midX = waypoint.x;
    midY = waypoint.y;
  } else {
    const bezier = getBezierPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
    edgePath = bezier[0];
    labelX = bezier[1];
    labelY = bezier[2];
    midX = labelX;
    midY = labelY;
  }

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const onMouseMove = (me: MouseEvent) => {
      const pos = screenToFlowPosition({ x: me.clientX, y: me.clientY });
      updateEdgeWaypoint(id, pos);
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [id, screenToFlowPosition, updateEdgeWaypoint]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    clearEdgeWaypoint(id);
  }, [id, clearEdgeWaypoint]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    removeConnection(id);
  }, [id, removeConnection]);

  const active = isHovered || !!selected;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: color,
          strokeWidth: active ? 2.5 : 1.5,
          strokeDasharray: connType === 'conditional' ? '6 3' : undefined,
          transition: 'stroke-width 0.1s',
        }}
        markerEnd={markerEnd}
      />

      {/* Wide invisible hit area — triggers hover */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        style={{ cursor: 'pointer' }}
        onMouseEnter={showHover}
        onMouseLeave={hideHover}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: '#fff',
              border: `1px solid ${color}`,
              borderRadius: 3,
              padding: '1px 5px',
              fontSize: 9,
              fontWeight: 500,
              color,
              pointerEvents: 'none',
              zIndex: 20,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Controls: always in DOM, opacity controls visibility */}
      <EdgeLabelRenderer>
        {/* Waypoint drag handle */}
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px)`,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: waypoint ? color : '#fff',
            border: `2px solid ${color}`,
            cursor: 'grab',
            pointerEvents: active || !!waypoint ? 'all' : 'none',
            zIndex: 25,
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            opacity: active || !!waypoint ? 1 : 0,
            transition: 'opacity 0.15s',
          }}
          title="Перетащите для изменения пути стрелки. Двойной клик — сброс."
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          onMouseEnter={showHover}
          onMouseLeave={hideHover}
        />

        {/* Delete button */}
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${midX + 16}px, ${midY - 12}px)`,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
            border: `2px solid #e53935`,
            color: '#e53935',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: active ? 'all' : 'none',
            zIndex: 26,
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            opacity: active ? 1 : 0,
            transition: 'opacity 0.15s',
            userSelect: 'none',
          }}
          title="Удалить связь"
          onClick={handleDelete}
          onMouseEnter={showHover}
          onMouseLeave={hideHover}
        >
          ×
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default memo(FlowEdge);
